import {State, Action, Selector, StateContext} from '@ngxs/store';
import {
  AnonLogin,
  CheckSession,
  DeleteUser,
  EditUser,
  GetUser,
  RegisterAnonToEmail,
  Login,
  LoginFailure,
  LoginRedirect,
  LoginSuccess,
  Logout,
  LogoutSuccess,
  RefreshToken,
  Register,
  ResetPassword,
  SetInit,
  LinkAnonToEmail,
  GoogleLogin,
  FacebookLogin,
  ChangePassword,
  UpdateUserPreference,
  LinkAnonAcctToFacebook,
  LinkAnonAcctToGoogle,
  LinkAcctToFacebook,
  LinkAcctToGoogle,
  UnlinkAcctFromFacebook,
  UnlinkAcctFromGoogle
} from '@shared/state/auth/auth.actions';
import {LoggerService} from '@app/service/logger/logger.service';
import {RoutingService} from '@app/service/routing/routing.service';
import {UserService} from '@shared/service/user/user.service';
import {ToastService} from '@app/service/util/toast.service';
import {AnalyticsService} from '@app/service/util/analytics.service';
import {authError, AuthErrorMessage, ProviderData} from '@shared/interfaces/auth';
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ServerResponse, UserProfile} from '@shared/interfaces';
import {StorageService} from '@app/service/util/storage.service';
import {checkNested, PROVIDER_DATA_KEY} from '@shared/helpers';
import {Injectable} from '@angular/core';
import {StateResetAll} from 'ngxs-reset-plugin';
import {ANALYTICS_COMPONENT} from '@shared/analytics';
import moment from 'moment';
import {AuthCredential, User, UserCredential} from '@angular/fire/auth';
import {DocumentData, DocumentSnapshot} from 'rxfire/firestore/interfaces';
import {OAuthProvider} from '@firebase/auth';

export interface AuthStateModel {
  userData: Partial<UserProfile>;
  tokenId: string;
  userId: string;
  initialized: boolean;
  error: any;
  redirectUrl: string;
  providerData: ProviderData[];
  pendingCred: AuthCredential;
  isAnonymous: boolean;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    userData: null,
    tokenId: null,
    userId: null,
    initialized: false,
    error: null,
    redirectUrl: null,
    providerData: null,
    isAnonymous: null,
    pendingCred: null
  }
})
@Injectable()
export class AuthState {
  ngUnsubscribe = new Subject();
  isActionLogin = false;
  firstRun = true;
  analyticsComponent: ANALYTICS_COMPONENT = 'auth';

  constructor(private logger: LoggerService, private userService: UserService, private routingService: RoutingService,
              private toastService: ToastService, private analytics: AnalyticsService,
              // private msgService: MessagingService,
              private storageService: StorageService) {
  }

  @Selector()
  static getUserData(state: AuthStateModel) {
    return state.userData;
  }

  @Selector()
  static getError(state: AuthStateModel) {
    return state.error;
  }

  @Selector()
  static getUserID(state: AuthStateModel) {
    return state.userId;
  }

  @Selector()
  static getUserToken(state: AuthStateModel) {
    return state.tokenId;
  }

  @Selector()
  static getInitialized(state: AuthStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  static getProviderData(state: AuthStateModel): any[] {
    return state.providerData;
  }

  @Selector()
  static getIsAnonymous(state: AuthStateModel): boolean {
    return state.isAnonymous;
  }

  @Selector()
  static getPendingCred(state: AuthStateModel): AuthCredential {
    return state.pendingCred;
  }

  @Action(CheckSession)
  checkSession(sc: StateContext<AuthStateModel>, {payload}: CheckSession) {
    return this.userService.getAuthState()
      .pipe(takeUntil(this.ngUnsubscribe), take(1))
      .subscribe(async (user) => {
        // this.firstRun = false;
        if (user) {
          if (payload && payload.url) {
            sc.patchState({
              redirectUrl: payload.url
            });
          }
          return sc.dispatch(new LoginSuccess(user));
        }
        sc.dispatch(new LoginFailure({message: 'silent'}));
        if (payload && payload.canNavigate) {
          return this.routingService.navigateLogin();
        }
      }, err => {
        this.logger.error('Check Session Err', err);
        this.routingService.navigateLogin();
      });
  }

  @Action(GetUser)
  getUserFromDB(sc: StateContext<AuthStateModel>, {payload}: GetUser): any {
    const userId = sc.getState().userId;
    return this.userService.getUserData(userId)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((result: DocumentSnapshot<DocumentData>) => {
        const userData = result.data();
        if (payload && payload.metadata && userData) {
          userData.creationDate = new Date(payload.metadata.creationTime);
          userData.lastSignInDate = new Date(payload.metadata.lastSignInTime);
        }
        if (!result || !userData) {
          return sc.patchState({
            initialized: true
          });
        }
        sc.patchState({
          userData,
          initialized: true
        });
        if (this.isActionLogin || sc.getState().redirectUrl) {
          this.goToPath(sc);
          this.isActionLogin = false;
        }
        return this.analytics.setUserProperties({
          userId: userData.userId
        });
      }, (error) => {
        this.logger.error('GetUser', error);
        // this.toastService.error(error.message);
        return sc.patchState({
          initialized: true
        });
      });
  }

  @Action(SetInit)
  setInitialized(sc: StateContext<AuthStateModel>, {payload}: SetInit): any {
    // Do a silent check session
    sc.dispatch(new CheckSession(payload));
  }

  /**
   * Redirect the user to the page they requested after the User data is added
   * @param sc
   */
  goToPath(sc: StateContext<AuthStateModel>) {
    const redirectUrl = sc.getState().redirectUrl;
    if (redirectUrl && (redirectUrl.includes('login') || redirectUrl.includes('register') || redirectUrl.includes('forgot'))) {
      return sc.patchState({
        redirectUrl: null
      });
    }
    sc.patchState({
      redirectUrl: null
    });
    return this.routingService.decodeAndNavigateToPath(redirectUrl);
  }

  @Action(Login)
  signInWithEmail(sc: StateContext<AuthStateModel>, {payload}: Login) {
    return this.userService.signInWithEmail(payload.email, payload.password)
      .then(async (user: UserCredential) => {
        this.isActionLogin = true;
        const pendingCred = sc.getState().pendingCred;
        if (pendingCred) {
          await this.userService.linkWithCredential(user.user, pendingCred);
          sc.patchState({
            pendingCred: null
          });
        }
        sc.patchState({
          redirectUrl: payload.redirectLink || ''
        });
        return sc.dispatch(new LoginSuccess(user.user));
      })
      .then(() => {
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'login', 'click', 'success');
      })
      .catch(error => {
        return sc.dispatch(new LoginFailure(error));
      });
  }

  @Action(AnonLogin)
  anonymousLogin(sc: StateContext<AuthStateModel>) {
    return this.userService.signInAnon()
      .then(async (user: UserCredential) => {
        const anonUserData: ServerResponse = await this.userService.createAnonUser({userId: user.user.uid});

        sc.patchState({
          userData: anonUserData.data,
          initialized: true
        });
        return user.user;
      })
      .then((user) => {
        return sc.dispatch(new LoginSuccess(user));
      })
      .catch(error => {
        return sc.dispatch(new LoginFailure(error));
      });
  }

  @Action(Logout)
  logout(sc: StateContext<AuthStateModel>) {
    this.ngUnsubscribe.next(0);
    this.ngUnsubscribe.complete();
    this.userService.signOut().then(
      async () => {
        // await this.msgService.deleteToken();
        return sc.dispatch(new LogoutSuccess());
      });
  }

  generateProviderData(userData: User): ProviderData[] {
    return [{
      providerId: userData.isAnonymous ? 'anonymous' : userData.providerId,
      uid: userData.uid
    }];
  }

  @Action(LoginSuccess)
  async setUserStateOnSuccess(sc: StateContext<AuthStateModel>, {payload}: LoginSuccess) {
    let providerData: ProviderData[] = payload.providerData;
    if (!providerData || providerData.length < 1) {
      providerData = this.generateProviderData(payload);
    }
    sc.patchState({
      tokenId: await payload.getIdToken(true),
      userId: payload.uid,
      providerData,
      isAnonymous: payload.isAnonymous
    });
    this.storageService.saveValue(PROVIDER_DATA_KEY, providerData);
    if (this.firstRun) {
      this.firstRun = false;
      sc.dispatch(new GetUser(payload));
    } else {
      // Since Anon users don't have firestore data associated with them, set all the required stuff here
      sc.patchState({
        initialized: true
      });
      if (this.isActionLogin || sc.getState().redirectUrl) {
        this.goToPath(sc);
        this.isActionLogin = false;
      }
      return this.analytics.setUserProperties({
        userId: payload.uid
      });
    }
  }

  @Action(LoginRedirect)
  redirectLogin(sc: StateContext<AuthStateModel>, {redirectUrl}: LoginRedirect) {
    if (redirectUrl) {
      sc.patchState({
        redirectUrl
      });
    }
    return this.routingService.navigateLogin();
  }

  @Action(LogoutSuccess)
  setUserStateOnLogout(sc: StateContext<AuthStateModel>) {
    sc.dispatch(
      new StateResetAll()
    );
    this.storageService.clear(PROVIDER_DATA_KEY);
    sc.patchState({
      initialized: true
    });
    return this.analytics.trackBusinessMetric(this.analyticsComponent, 'logout', 'click', 'success');
  }

  @Action(LoginFailure)
  setUserStateOnFailure(sc: StateContext<AuthStateModel>, {payload}: LoginFailure) {
    this.storageService.clear(PROVIDER_DATA_KEY);
    if (payload && (payload.data || payload.code)) {
      const errorData: AuthErrorMessage = authError(payload.data || payload, this.routingService);
      if (checkNested(errorData, 'showMessage')) {
        this.toastService.error(errorData.message, null, 10000)
          .onClick
          .subscribe(() => {
            if (errorData.action) {
              errorData.action();
            }
          });
      }
      sc.patchState({
        error: {
          message: errorData.message,
          code: payload.code
        },
        initialized: true
      });
      return this.analytics.trackMissionCritical(this.analyticsComponent, 'login', 'login', 'error', {
        error: errorData.message
      });
    } else {
      sc.patchState({
        initialized: true
      });
    }
  }

  @Action(Register)
  registerUser(sc: StateContext<AuthStateModel>, {payload}: Register) {
    return this.userService.createUser(payload)
      .then((_userCred: UserCredential) => {
        if (!_userCred || !_userCred.user) {
          return Promise.resolve(true);
        }
        const userData: Partial<UserProfile> = {
          name: payload.name,
          email: payload.email,
          userId: _userCred.user.uid
        };
        return this.userService.createUserEntry(userData);
      })
      .then(() => {
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'register', 'click', 'success');
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'register', 'click', 'error');
      });
  }

  @Action(RegisterAnonToEmail)
  registerAnonToEmail(sc: StateContext<AuthStateModel>, {payload}: RegisterAnonToEmail) {
    const oldUserId = sc.getState().userId;
    return this.userService.linkAnonToEmail(payload)
      .then((_userCred) => {
        if (!_userCred || !_userCred.user) {
          return Promise.resolve(true);
        }
        // After linking the anon user to an email, send the user data as payload to complete migrating user data
        const userData: Partial<UserProfile> & { [k: string]: any } = {
          name: payload.name,
          email: payload.email,
          userId: _userCred.user.uid,
          oldUserId,
          linkType: payload.linkType
        };
        return this.userService.linkUserToAcct(userData);
      })
      .then(() => {
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'register-anon', 'click', 'success');
      })
      .catch((error) => {
        this.logger.error(`ERROR Linking Anon - ${error}`);
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'register-anon', 'click', 'error');
      });
  }

  @Action(LinkAnonToEmail)
  linkAnonToEmail(sc: StateContext<AuthStateModel>, {payload}: LinkAnonToEmail) {
    const oldUserId = sc.getState().userId;
    let mainUserId = '';

    return this.userService.signInWithEmail(payload.email, payload.password)
      .then((linkResult) => {
        // After linking the anon user to an email, send the user data as payload to complete migrating user data
        const userData: Partial<UserProfile> & { [k: string]: any } = {
          name: payload.name,
          email: payload.email,
          userId: linkResult.user.uid,
          oldUserId,
          linkType: payload.linkType
        };
        mainUserId = linkResult.user.uid;
        return this.userService.linkUserToAcct(userData);
      })
      .then(() => {
        sc.patchState({
          userId: mainUserId
        });
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'anon', 'login', 'success');
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'anon', 'login', 'error');
      });
  }

  @Action(LinkAnonAcctToFacebook)
  _linkAnonAcctToFacebook(sc: StateContext<AuthStateModel>, {payload}: LinkAnonAcctToFacebook) {
    return this.userService.linkAnonAcctToFacebook(payload.linkType)
      .then((userId) => {
        sc.patchState({
          userId
        });
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'login-fb-anon', 'link', 'success');
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'login-fb-anon', 'link', 'error');
      });
  }

  @Action(LinkAnonAcctToGoogle)
  _linkAnonAcctToGoogle(sc: StateContext<AuthStateModel>, {payload}: LinkAnonAcctToGoogle) {
    return this.userService.linkAnonAcctToGoogle(payload.linkType)
      .then((userId: string) => {
        sc.patchState({
          userId
        });
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'link-fb', 'link', 'success');
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'link-fb', 'link', 'error');
      });
  }

  @Action(LinkAcctToFacebook)
  _linkAcctToFacebook(sc: StateContext<AuthStateModel>, {payload}: LinkAnonAcctToFacebook) {
    return this.userService.linkAcctToFacebook(payload.linkType)
      .then((userId) => {
        sc.patchState({
          userId
        });
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'login-fb-anon', 'link', 'success');
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'login-fb-anon', 'link', 'error');
      });
  }

  @Action(LinkAcctToGoogle)
  _linkAcctToGoogle(sc: StateContext<AuthStateModel>, {payload}: LinkAcctToGoogle) {
    return this.userService.linkAcctToGoogle(payload.linkType)
      .then(() => {
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        // sc.dispatch(new CheckSession());
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'link-fb', 'link', 'success');
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'link-fb', 'link', 'error');
      });
  }

  @Action(UnlinkAcctFromFacebook)
  _unlinkAcctFromFacebook(sc: StateContext<AuthStateModel>, {payload}: UnlinkAcctFromFacebook) {
    return this.userService.unlinkSocialAccount('facebook')
      .then(() => {
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        sc.dispatch(new CheckSession());
      })
      .catch((error) => {
        this.logger.error('UnlinkAcctFromFacebook', JSON.stringify(error.stack));
      });
  }

  @Action(UnlinkAcctFromGoogle)
  _unlinkAcctFromGoogle(sc: StateContext<AuthStateModel>, {payload}: UnlinkAcctFromGoogle) {
    return this.userService.unlinkSocialAccount('google.com')
      .then(() => {
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
        sc.dispatch(new CheckSession());
      })
      .catch((error) => {
        this.logger.error('UnlinkAcctFromGoogle', JSON.stringify(error.stack));
      });
  }

  @Action(GoogleLogin)
  _loginWithGoogle(sc: StateContext<AuthStateModel>, {payload}: GoogleLogin) {
    return this.userService.signInWithGoogle()
      .then(async (user: UserCredential) => {
        this.isActionLogin = true;
        const pendingCred = sc.getState().pendingCred;
        if (pendingCred) {
          await this.userService.linkWithCredential(user.user, pendingCred);
          sc.patchState({
            pendingCred: null
          });
        }
        sc.patchState({
          redirectUrl: payload.redirectLink || ''
        });
        await this.handleSocialLogin(user);

        return sc.dispatch(new LoginSuccess(user.user));
      })
      .then(() => {
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'google', 'login', 'success');
      })
      .catch(error => {
        sc.dispatch(new LoginFailure(error));
        return this.handleSocialLoginError(error, sc);
      });
  }

  @Action(FacebookLogin)
  _loginWithFacebook(sc: StateContext<AuthStateModel>, {payload}: FacebookLogin) {
    return this.userService.signInWithFacebook()
      .then(async (user: UserCredential) => {
        this.isActionLogin = true;
        const pendingCred = sc.getState().pendingCred;
        if (pendingCred) {
          await this.userService.linkWithCredential(user.user, pendingCred);
          sc.patchState({
            pendingCred: null
          });
        }
        sc.patchState({
          redirectUrl: payload.redirectLink || ''
        });
        await this.handleSocialLogin(user);
        return sc.dispatch(new LoginSuccess(user.user));
      })
      .then(() => {
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'fb', 'login', 'success');
      })
      .catch(error => {
        sc.dispatch(new LoginFailure(error));
        return this.handleSocialLoginError(error, sc);
      });
  }

  async handleSocialLoginError(error: any, sc: StateContext<AuthStateModel>) {
    if (!error || !error.code) {
      return;
    }
    if (error.code && error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData.email;
      const methods = await this.userService.fetchSignInMethods(email);
      const recMethod = methods[0];
      if (recMethod === 'password') {
        this.toastService.info('Please provide your email and password instead');
      } else if (recMethod === 'google.com') {
        this.toastService.info('Please use the google login method instead');
      } else if (recMethod === 'facebook.com') {
        this.toastService.info('Please use the facebook login method instead');
      }
      try {
        const credential = OAuthProvider.credentialFromError(error);
        sc.patchState({
          pendingCred: credential
        });
      } catch (e) {
        this.logger.error(`Error Credential - ${JSON.stringify(e.stack)}`);
      }
    }
  }

  @Action(EditUser)
  editUser({getState, patchState}: StateContext<AuthStateModel>, {payload}: EditUser) {
    return this.userService.editUserData(getState().userData, payload.userData)
      .then(async (response: ServerResponse) => {
        this.toastService.response(response.type, response.message);
        if (response.type === 'success') {
          this.analytics.trackBusinessMetric(this.analyticsComponent, 'edit_user', 'edit', 'success');
        }
        patchState({
          userData: {
            ...getState().userData,
            ...response.data.userData
          }
        });
      })
      .catch(() => {
        // this.toastService.error(error.message);
      });
  }

  @Action(ResetPassword)
  resetPassword(sc: StateContext<AuthStateModel>, {payload}: any) {
    return this.userService.resetPassword(payload)
      .then(() => {
        this.toastService.info('Password reset email sent, check your inbox.');
        sc.dispatch(new LoginRedirect(''));
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'reset-password', 'click', 'success');
      }).catch((error) => {
        this.toastService.error('There was an issue while trying to reset your account. Please contact support!');
        return this.analytics.trackBusinessMetric(this.analyticsComponent, 'reset-password', 'click', 'error', {error});
      });
  }

  @Action(RefreshToken)
  async refreshToken(sc: StateContext<AuthStateModel>) {
    const currentUser = await this.userService.currentUser();
    return currentUser.getIdToken(true)
      .then(async (tokenId) => {
        sc.patchState({
          tokenId
        });
        return tokenId;
      }).catch(() => {
        // this.logger.error(error);
      });
  }

  @Action(ChangePassword)
  changePassword({getState, setState, patchState}: StateContext<AuthStateModel>, {payload}: ChangePassword) {
    const email = getState().userData.email;
    return this.userService.signInWithEmail(email, payload.oldPassword)
      .then(async (user: UserCredential) => {
        if (!user) {
          throw new Error('Issue retrieving your user profile');
        }
        delete payload.oldPassword;
        return this.userService.changePassword(payload);
      })
      .then((response: ServerResponse) => {
        this.toastService.response(response.type, response.message);
        if (response.type === 'success') {
          return this.analytics.trackEvent('change_password');
        }
      })
      .catch(error => {
        this.toastService.error(`Unable to change your password!`, error.message);
      });
  }

  @Action(UpdateUserPreference)
  updateUserPref({getState, setState, patchState}: StateContext<AuthStateModel>, {payload}: UpdateUserPreference) {
    const userId = getState().userId;
    let payloadKey = payload.key;
    if (!payloadKey.includes('preferences.')) {
      payloadKey = `preferences.${payloadKey}`;
    }
    return this.userService.editUserPreference(userId, payloadKey, payload.value)
      .then(() => {
        const userData = getState().userData;
        patchState({
          userData: {
            ...userData,
            [payloadKey]: payload.value
          }
        });
      })
      .catch(error => {
        this.toastService.error(`Unable to change your password!`, error.message);
      });
  }

  @Action(DeleteUser)
  deleteUser() {
    return this.userService.getAuthState()
      .pipe(takeUntil(this.ngUnsubscribe), take(1))
      .subscribe(async (user) => {
        // this.firstRun = false;
        if (user) {
          await user.delete();
        }
      }, err => {
        this.logger.error(`Check Session Error - ${err}`);
        this.routingService.navigateLogin();
      });
  }

  /**
   * This method handles social account setups from new users. It checks the creation time for a user,
   * if it was recent, an entry is created
   * @param user
   * @returns {Promise<void>}
   */
  async handleSocialLogin(user: UserCredential) {
    const creationTime = new Date(user.user.metadata.creationTime);
    const signupDate = moment(creationTime);
    const rightNow = moment();
    const duration = moment.duration(rightNow.diff(signupDate));
    const seconds = duration.asSeconds();

    // Account was created within the last minute
    if (seconds <= 120) {
      const userData: Partial<UserProfile> = {
        name: user.user.displayName,
        email: user.user.email, // If this is not included, make sure to check the multiple accounts to email setting
        userId: user.user.uid,
        photoUrl: user.user.photoURL
      };
      await this.userService.createUserEntry(userData);
    }
  }
}
